// src/components/shops/postTemplates/RecipeCard.tsx
//
// An index card: dose, yield and time across the top, then a numbered method.
//
// Shops already hand these out at the counter on scraps of receipt paper. This
// is the version a customer keeps in their photos and actually follows at home,
// which is the whole argument for a shop posting brew guides at all.

import SocialBrewBadge from '../SocialBrewBadge'
import { bodyLines, type BodyRow, type TemplateProps } from './types'

// A labelled line short enough to be a measurement belongs in the header
// strip; anything longer is a step, and long ones would wreck the strip.
const SPEC_MAX = 14
const SPEC_SLOTS = 3

export default function RecipeCard({
  title, body, shopName, rating, ratingCount, scale = 1,
}: TemplateProps) {
  const px = (n: number) => `${n * scale}px`
  // Short labelled lines ("Dose: 18g") fill the header strip; everything else
  // is a step, so a shop writes one list and the card sorts it out.
  const specs: BodyRow[] = []
  const steps: string[] = []
  for (const line of bodyLines(body)) {
    const m = line.match(/^([^:–—]{1,24}?)\s*[:–—]\s*(.+)$/)
    if (m && m[2].length <= SPEC_MAX && specs.length < SPEC_SLOTS) {
      specs.push({ label: m[1], value: m[2] })
    } else {
      steps.push(line)
    }
  }

  return (
    <div
      style={{
        background: '#fefcf8',
        borderRadius: px(3),
        border: `${px(1)} solid #e8d8c0`,
        // The ruled line down the side is what makes it read as an index card
        // rather than a plain white box.
        borderLeft: `${px(5)} solid #c8853a`,
        padding: `${px(18)} ${px(16)} ${px(14)}`,
      }}
      data-shop={shopName}
    >
      <p
        style={{
          fontSize: px(8.5),
          letterSpacing: '0.3em',
          textTransform: 'uppercase',
          color: '#a88660',
          fontWeight: 700,
          marginBottom: px(6),
        }}
      >
        How we brew it
      </p>
      <h3
        className="font-display"
        style={{ fontSize: px(21), lineHeight: 1.14, color: '#241810', fontWeight: 700, margin: 0, textWrap: 'balance' }}
      >
        {title}
      </h3>

      {specs.length > 0 && (
        <div
          className="flex"
          style={{
            gap: px(10),
            marginTop: px(12),
            paddingTop: px(10),
            paddingBottom: px(10),
            borderTop: `${px(1)} solid #e8d8c0`,
            borderBottom: `${px(1)} solid #e8d8c0`,
          }}
        >
          {specs.map((s, i) => (
            <div key={i} style={{ flex: 1 }}>
              <p
                style={{
                  fontSize: px(8),
                  letterSpacing: '0.18em',
                  textTransform: 'uppercase',
                  color: '#a88660',
                  fontWeight: 700,
                  margin: 0,
                }}
              >
                {s.label}
              </p>
              <p
                className="font-display"
                style={{ fontSize: px(15), color: '#241810', fontWeight: 700, margin: `${px(2)} 0 0` }}
              >
                {s.value}
              </p>
            </div>
          ))}
        </div>
      )}

      {steps.length > 0 && (
        <ol style={{ margin: `${px(12)} 0 0`, padding: 0, listStyle: 'none' }}>
          {steps.map((step, i) => (
            <li key={i} className="flex" style={{ gap: px(9), marginBottom: px(7) }}>
              <span
                className="font-display"
                style={{ fontSize: px(12), color: '#c8853a', fontWeight: 700, flexShrink: 0, width: px(14) }}
              >
                {i + 1}
              </span>
              <span style={{ fontSize: px(12.5), lineHeight: 1.55, color: '#52392a' }}>{step}</span>
            </li>
          ))}
        </ol>
      )}

      <div style={{ marginTop: px(14) }}>
        <SocialBrewBadge rating={rating} ratingCount={ratingCount} tone="light" scale={scale} />
      </div>
    </div>
  )
}
